import { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Currency configuration
const CURRENCIES = {
  EUR: { symbol: '€', name: 'Euro', decimals: 2 },
  USD: { symbol: '$', name: 'US Dollar', decimals: 2 },
  GBP: { symbol: '£', name: 'British Pound', decimals: 2 },
  NGN: { symbol: '₦', name: 'Nigerian Naira', decimals: 0 },
  ZAR: { symbol: 'R', name: 'South African Rand', decimals: 2 },
  AED: { symbol: 'د.إ', name: 'UAE Dirham', decimals: 2 },
  INR: { symbol: '₹', name: 'Indian Rupee', decimals: 0 },
  CAD: { symbol: 'C$', name: 'Canadian Dollar', decimals: 2 },
  AUD: { symbol: 'A$', name: 'Australian Dollar', decimals: 2 },
};

// Country to currency mapping
const COUNTRY_CURRENCY = {
  ES: 'EUR', DE: 'EUR', FR: 'EUR', IT: 'EUR', PT: 'EUR', NL: 'EUR', BE: 'EUR',
  GB: 'GBP', PL: 'EUR',
  NG: 'NGN', ZA: 'ZAR', EG: 'EUR', KE: 'USD', GH: 'USD',
  US: 'USD', CA: 'CAD', MX: 'USD', BR: 'USD',
  AE: 'AED', SA: 'USD', IN: 'INR', SG: 'USD', AU: 'AUD',
};

// Default exchange rates (USD base) - Fallback when API unavailable
const DEFAULT_EXCHANGE_RATES = {
  USD: 1.00,
  EUR: 0.92,
  GBP: 0.79,
  NGN: 1550.00,
  ZAR: 18.50,
  AED: 3.67,
  INR: 83.50,
  CAD: 1.36,
  AUD: 1.53,
};

// Shipping costs per country (USD)
const SHIPPING_COSTS = {
  ES: 3.50, DE: 3.50, FR: 3.80, IT: 4.00, PT: 4.20, NL: 3.50, BE: 3.50, GB: 4.00,
  NG: 8.50, ZA: 7.50, EG: 6.50, KE: 9.00, GH: 9.50,
  US: 5.00, CA: 5.50, MX: 6.00, BR: 8.00,
  AE: 4.50, SA: 5.00, IN: 4.00, SG: 3.50, AU: 6.50,
};

// Country names for display
const COUNTRIES = {
  ES: { name: 'Spain', flag: '🇪🇸', region: 'Europe' },
  DE: { name: 'Germany', flag: '🇩🇪', region: 'Europe' },
  FR: { name: 'France', flag: '🇫🇷', region: 'Europe' },
  IT: { name: 'Italy', flag: '🇮🇹', region: 'Europe' },
  PT: { name: 'Portugal', flag: '🇵🇹', region: 'Europe' },
  NL: { name: 'Netherlands', flag: '🇳🇱', region: 'Europe' },
  BE: { name: 'Belgium', flag: '🇧🇪', region: 'Europe' },
  GB: { name: 'United Kingdom', flag: '🇬🇧', region: 'Europe' },
  NG: { name: 'Nigeria', flag: '🇳🇬', region: 'Africa' },
  ZA: { name: 'South Africa', flag: '🇿🇦', region: 'Africa' },
  EG: { name: 'Egypt', flag: '🇪🇬', region: 'Africa' },
  KE: { name: 'Kenya', flag: '🇰🇪', region: 'Africa' },
  GH: { name: 'Ghana', flag: '🇬🇭', region: 'Africa' },
  US: { name: 'United States', flag: '🇺🇸', region: 'Americas' },
  CA: { name: 'Canada', flag: '🇨🇦', region: 'Americas' },
  MX: { name: 'Mexico', flag: '🇲🇽', region: 'Americas' },
  BR: { name: 'Brazil', flag: '🇧🇷', region: 'Americas' },
  AE: { name: 'UAE', flag: '🇦🇪', region: 'Middle East' },
  SA: { name: 'Saudi Arabia', flag: '🇸🇦', region: 'Middle East' },
  IN: { name: 'India', flag: '🇮🇳', region: 'Asia' },
  SG: { name: 'Singapore', flag: '🇸🇬', region: 'Asia' },
  AU: { name: 'Australia', flag: '🇦🇺', region: 'Oceania' },
};

const CurrencyContext = createContext();

export const CurrencyProvider = ({ children }) => {
  const [country, setCountry] = useState(() => {
    return localStorage.getItem('salesspace_country') || 'ES';
  });
  
  const [exchangeRates, setExchangeRates] = useState(() => {
    // Try to load cached rates
    const cached = localStorage.getItem('salesspace_exchange_rates');
    if (cached) {
      try {
        const { rates } = JSON.parse(cached);
        return rates;
      } catch {
        return DEFAULT_EXCHANGE_RATES;
      }
    }
    return DEFAULT_EXCHANGE_RATES;
  });
  
  const [ratesLastUpdated, setRatesLastUpdated] = useState(null);

  const currency = COUNTRY_CURRENCY[country] || 'EUR';
  const currencyInfo = CURRENCIES[currency] || CURRENCIES.EUR;
  const exchangeRate = exchangeRates[currency] || DEFAULT_EXCHANGE_RATES[currency] || 1;
  const shippingCost = SHIPPING_COSTS[country] || 5.00;
  const countryInfo = COUNTRIES[country] || COUNTRIES.ES;

  // Fetch live exchange rates
  const fetchExchangeRates = useCallback(async () => {
    try {
      const response = await fetch('https://open.er-api.com/v6/latest/USD');
      const data = await response.json();
      
      if (data.result === 'success' && data.rates) {
        const newRates = {};
        Object.keys(CURRENCIES).forEach(code => {
          newRates[code] = data.rates[code] || DEFAULT_EXCHANGE_RATES[code];
        });
        setExchangeRates(newRates);
        setRatesLastUpdated(new Date());
        
        // Cache for 1 hour
        localStorage.setItem('salesspace_exchange_rates', JSON.stringify({
          rates: newRates,
          timestamp: Date.now()
        }));
      }
    } catch (error) {
      console.error('Failed to fetch exchange rates:', error);
    }
  }, []);

  // Load exchange rates on mount
  useEffect(() => {
    const cached = localStorage.getItem('salesspace_exchange_rates');
    if (cached) {
      try {
        const { timestamp } = JSON.parse(cached);
        const hoursSinceUpdate = (Date.now() - timestamp) / (1000 * 60 * 60);
        
        if (hoursSinceUpdate < 1) {
          setRatesLastUpdated(new Date(timestamp));
          return; // Use cached rates
        }
      } catch {
        // Invalid cache, fetch fresh
      }
    }
    
    fetchExchangeRates();
  }, [fetchExchangeRates]);

  // Save country preference
  useEffect(() => {
    localStorage.setItem('salesspace_country', country);
  }, [country]);

  // Convert USD to local currency
  const convertPrice = (priceUSD) => {
    return priceUSD * exchangeRate;
  };

  // Format price with currency symbol
  const formatPrice = (priceUSD, includeConversion = true) => {
    const localPrice = includeConversion ? convertPrice(priceUSD) : priceUSD;
    const { symbol, decimals } = currencyInfo;
    
    if (decimals === 0) {
      return `${symbol}${Math.round(localPrice).toLocaleString()}`;
    }
    return `${symbol}${localPrice.toFixed(decimals)}`;
  };

  // Get shipping cost in local currency
  const getShippingCost = () => {
    return convertPrice(shippingCost);
  };

  // Format shipping cost
  const formatShipping = () => {
    return formatPrice(shippingCost);
  };

  // Calculate total with shipping
  const calculateTotal = (priceUSD, quantity = 1) => {
    const subtotal = priceUSD * quantity;
    const shipping = shippingCost;
    return {
      subtotalUSD: subtotal,
      shippingUSD: shipping,
      totalUSD: subtotal + shipping,
      subtotalLocal: convertPrice(subtotal),
      shippingLocal: convertPrice(shipping),
      totalLocal: convertPrice(subtotal + shipping),
      formatted: {
        subtotal: formatPrice(subtotal),
        shipping: formatPrice(shipping),
        total: formatPrice(subtotal + shipping),
      }
    };
  };

  return (
    <CurrencyContext.Provider value={{
      country,
      setCountry,
      currency,
      currencyInfo,
      exchangeRate,
      exchangeRates,
      shippingCost,
      countryInfo,
      convertPrice,
      formatPrice,
      getShippingCost,
      formatShipping,
      calculateTotal,
      fetchExchangeRates,
      ratesLastUpdated,
      COUNTRIES,
      CURRENCIES,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export default CurrencyContext;
